import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './QuestionKeyWordListDisplay.scss';
import { t } from '../../services/i18nService';
import QuestionKeyWord from './QuestionKeyword';
import sessionReader from '../../store/readHelpers/SessionReader';
import scorableItemNumberingService from '../../services/questionView/ScorableItemNumberingService';

const QuestionKeyWordList = (props) => {
    const { question, sectionId, currentScorableItem, ui } = props;
    const zoomLMaxHeight = 455; // see mixins.scss, variable $desktop-height-L
    const keywordListScrollResetDelay = 100; // should be enough for the scroll to happen and for the keyword list to be visible, see TEII-4633
    const wrapperRef = useRef(null);
    const [wrapperStyle, setWrapperStyle] = useState({});
    const [isFixed, setIsFixed] = useState(false);
    // need this because there is no access to the latest state inside event handlers
    const isFixedRef = useRef(isFixed);
    // CEQ enable fixed positioning && not print or pdf view
    const isCeqEnhancedTextSize = ui.marketplaceTheme === 'ceq' && !sessionReader.isViewMediaPrintOrPdf();

    let questionWrapper;
    let sectionContentTopPadding = 0;

    const wrapperClassNames = [styles.keyWordListWrapper];
    if (isCeqEnhancedTextSize) {
        wrapperClassNames.push(styles.keyWordListWrapperCeqEnhanced);
    }
    if (isFixed) {
        wrapperClassNames.push(styles.keyWordListWrapperFixed);
    }

    const sortedResponseModels = question.responseModels.sort((a, b) => {
        const placeholderA = scorableItemNumberingService.getInteractionNumber(question.contentRevisionId, a.identifier);
        const placeholderB = scorableItemNumberingService.getInteractionNumber(question.contentRevisionId, b.identifier);
        return placeholderA - placeholderB;
    });

    useEffect(() => {
        if (sessionReader.isViewMediaPrintOrPdf()) {
            /* eslint-disable consistent-return */
            return;
        }

        questionWrapper = document.querySelector('.question-wrapper.current');
        handleScroll();

        const sectionContent = document.getElementById('sectionContent');
        if (sectionContent) {
            const sectionContentStyle = window.getComputedStyle(sectionContent, null);
            sectionContentTopPadding = parseInt(sectionContentStyle.getPropertyValue('padding-top'), 10) || 0;
            addScrollHandlers(sectionContent);
            return () => {
                removeScrollHandlers(sectionContent);
            };
        }

        return null;
    }, []);

    useEffect(() => {
        const sectionContent = document.getElementById('sectionContent');
        if (!isCeqEnhancedTextSize) {
            return undefined;
        }

        if (!sectionContent || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(() => {
            updateCeqEnhancedWrapperStyle();
        });

        observer.observe(sectionContent);
        return () => observer.disconnect();
    }, [isCeqEnhancedTextSize]);

    useEffect(() => {
        if (!isCeqEnhancedTextSize || !wrapperRef.current) {
            return;
        }

        const timeout = setTimeout(() => {
            if (wrapperRef.current) {
                wrapperRef.current.scrollLeft = 0;
            }
        }, keywordListScrollResetDelay);

        return () => clearTimeout(timeout);
    }, [currentScorableItem, isCeqEnhancedTextSize]);

    const addScrollHandlers = (sectionContent) => {
        // for smaller resolutions there is global page scroll bar
        document.addEventListener('scroll', handleScroll);
        // for bigger resolutions there is inner scrollbar on the sectionContent level
        sectionContent.addEventListener('scroll', handleScroll);
        // re-calc the height on page resize and browser zoom change as well
        window.addEventListener('resize', handleScroll);
    };

    const removeScrollHandlers = (sectionContent) => {
        sectionContent.removeEventListener('scroll', handleScroll);
        document.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
    };

    const setFixedState = (fixedState) => {
        isFixedRef.current = fixedState;
        setIsFixed(fixedState);
    };

    const updateCeqEnhancedWrapperStyle = () => {
        if (!isCeqEnhancedTextSize) return;

        const sectionContent = document.getElementById('sectionContent');
        const sectionContentRight = sectionContent?.getBoundingClientRect().right || window.innerWidth;

        // This calculates exactly where the notes panel starts
        const notesPanelOffset = window.innerWidth - sectionContentRight;
        const rightOffset = notesPanelOffset > 0 ? `calc(${notesPanelOffset}px + 1rem)` : '2rem';
        const keyWordsListTopOffset = wrapperRef.current.getBoundingClientRect().top;

        setWrapperStyle({
            position: 'fixed',
            right: rightOffset,
            maxHeight: `calc(100vh - ${keyWordsListTopOffset}px - 5.5rem - 4.5rem)`,
        });
    };

    const handleScroll = () => {
        if (!questionWrapper) {
            return;
        }

        const pageHeight = window.innerHeight;
        const headerHeight = document.getElementById('header')?.clientHeight || 0;
        const headerOffset = (pageHeight > zoomLMaxHeight && headerHeight) || 0;
        const questionWrapperOffset = questionWrapper.getBoundingClientRect().top - headerOffset - sectionContentTopPadding;

        if (questionWrapperOffset > 0) {
            if (isFixedRef.current) {
                setFixedState(false);
            }

            if (isCeqEnhancedTextSize) {
                updateCeqEnhancedWrapperStyle();
            } else {
                updateListHeight();
            }
        } else if (!isFixedRef.current) {
            setFixedState(true);

            if (isCeqEnhancedTextSize) {
                updateCeqEnhancedWrapperStyle();
            } else {
                setWrapperStyle({});
            }
        } else if (isCeqEnhancedTextSize) {
            updateCeqEnhancedWrapperStyle();
        }
    };

    const updateListHeight = () => {
        const keyWordsListTopOffset = wrapperRef.current.getBoundingClientRect().top;

        // see QuestionKeyWordListDisplay.scss, class keyWordListWrapperFixed
        // 5.5rem - navButtons bottom offset
        // 4.5rem - ceq header height
        // ref: https://inspera.atlassian.net/browse/TEII-4633

        setWrapperStyle({ maxHeight: `calc(100vh - ${keyWordsListTopOffset}px - 5.5rem - 4.5rem)` });
    };

    return (<div
        className={wrapperClassNames.join(' ')}
        style={wrapperStyle}
        ref={wrapperRef}
        data-ui-theme={ui.marketplaceTheme}
        data-ui-zoom={ui.zoomFactor}
        data-enhanced={isCeqEnhancedTextSize ? 'true' : 'false'}
    >
        <ul>
            <li className={styles.keyWordListName}>
                { t('question.keyWordList') }
            </li>

            { sortedResponseModels.map((response) => {
                return (
                    <QuestionKeyWord
                        key={response.identifier}
                        question={question}
                        response={response}
                        currentScorableItem={currentScorableItem}
                        sectionId={sectionId}
                    />
                );
            })}
        </ul>
    </div>);
};

QuestionKeyWordList.propTypes = {
    question: PropTypes.object.isRequired,
    currentScorableItem: PropTypes.number,
    sectionId: PropTypes.number.isRequired,
    ui: PropTypes.object.isRequired,
};

export default QuestionKeyWordList;
